import { createCard } from "./card";
import { t } from "./i18n";
import { icon, stack } from "./ui";
import type { GameCard } from "../shared/deck";

const first: GameCard = { id: 1001, symbols: [0, 4, 8, 35] };
const second: GameCard = { id: 1003, symbols: [1, 4, 11, 56] };
const initialCenter: GameCard = { id: 1002, symbols: [2, 35, 11, 27] };
const hand = [first, second];

/** A two-card Puits round with no effect on the real score or XP. */
export function createTutorial(root: HTMLElement) {
  const own = root.querySelector<HTMLElement>("#tutorial-own")!;
  const center = root.querySelector<HTMLElement>("#tutorial-center")!;
  const status = root.querySelector<HTMLElement>("#tutorial-status")!;
  const ownCount = root.querySelector<HTMLElement>("#tutorial-own-count")!;
  const centerCount = root.querySelector<HTMLElement>("#tutorial-center-count")!;
  const replay = root.querySelector<HTMLButtonElement>("#tutorial-replay")!;
  let index = 0;
  let middle = initialCenter;
  let active = false;
  let busy = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let animation: Animation | undefined;
  let flight: HTMLElement | undefined;

  function cancel() {
    clearTimeout(timer);
    animation?.cancel();
    flight?.remove();
    flight = undefined;
    animation = undefined;
    busy = false;
  }

  function pile(host: HTMLElement, card: GameCard | null, count: number, variant: string) {
    host.innerHTML = stack("", Math.max(0, count - 1), variant);
    const face = host.querySelector<HTMLElement>(".card-face")!;
    face.removeAttribute("id");
    if (card) face.append(createCard(card, { interactive: true, onSymbol: choose }));
    else face.innerHTML = `<div class="tutorial-empty">${icon("check", 36)}</div>`;
  }

  function render() {
    pile(own, hand[index] ?? null, hand.length - index, "own");
    pile(center, middle, index + 1, "center");
    ownCount.textContent = String(hand.length - index);
    centerCount.textContent = String(index + 1);
    status.textContent = t(index === hand.length ? "PILE VIDE !" : index ? "Encore une carte" : "À toi d’essayer");
    root.classList.toggle("is-found", index === hand.length);
  }

  function choose(id: number) {
    const card = hand[index];
    if (!active || busy || !card) return;
    if (!card.symbols.includes(id) || !middle.symbols.includes(id)) return;
    clearTimeout(timer);
    busy = true;
    status.textContent = t("BIEN VU !");
    for (const symbol of root.querySelectorAll<HTMLElement>(`.card-symbol[data-symbol-id="${id}"]`))
      symbol.classList.add("tutorial-symbol-found");
    const land = () => {
      cancel();
      middle = card;
      index++;
      render();
    };
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      land();
      return;
    }
    timer = setTimeout(() => {
      const from = own.querySelector<HTMLElement>(".game-card")!.getBoundingClientRect();
      const to = center.querySelector<HTMLElement>(".game-card")!.getBoundingClientRect();
      const bounds = root.getBoundingClientRect();
      flight = createCard(card);
      flight.classList.add("tutorial-flight");
      Object.assign(flight.style, { left: `${from.left - bounds.left}px`, top: `${from.top - bounds.top}px`, width: `${from.width}px` });
      root.append(flight);
      pile(own, hand[index + 1] ?? null, hand.length - index - 1, "own");
      ownCount.textContent = String(hand.length - index - 1);
      animation = flight.animate([
        { transform: "translate(0,0)" },
        { transform: `translate(${(to.left - from.left) / 2}px,-22px) rotate(-5deg)`, offset: 0.5 },
        { transform: `translate(${to.left - from.left}px,${to.top - from.top}px)` },
      ], { duration: 420, easing: "ease-in-out", fill: "forwards" });
      timer = setTimeout(land, 420);
    }, 300);
  }

  function reset() {
    cancel();
    index = 0;
    middle = initialCenter;
    render();
  }
  replay.addEventListener("click", reset);
  reset();
  return {
    setActive(next: boolean) {
      if (active && !next) reset();
      active = next;
    },
    destroy() {
      cancel();
      replay.removeEventListener("click", reset);
    },
  };
}

export type Tutorial = ReturnType<typeof createTutorial>;
